import type {
  CostLineItem,
  CustomEntry,
  Journey,
  Money,
  Place,
  Stay,
  Trip,
} from '@my-fullstack-app/itinerary-types';

/**
 * Read helpers over the per-line-item cost model (ADR-0069).
 * `Place.costs` and `CustomEntry.costs` live directly on the
 * entity; `Journey` and `Stay` costs live under `booking.costs`.
 *
 * Each line item is validated independently — items with non-
 * finite or non-positive amounts, or missing currency, are
 * skipped silently so legacy garbage doesn't pollute totals.
 * Currency is normalised to upper-case so 'usd' and 'USD'
 * group correctly.
 */
export type CostBearing =
  | { kind: 'place'; entity: Place }
  | { kind: 'custom'; entity: CustomEntry }
  | { kind: 'journey'; entity: Journey }
  | { kind: 'stay'; entity: Stay };

/** Raw line items as stored on the entity. Returns an empty
 *  array when no costs are set — callers can iterate without
 *  null-checking. */
export function entityLineItems(bearer: CostBearing): CostLineItem[] {
  switch (bearer.kind) {
    case 'place':
      return bearer.entity.costs ?? [];
    case 'custom':
      return bearer.entity.costs ?? [];
    case 'journey':
      return bearer.entity.booking?.costs ?? [];
    case 'stay':
      return bearer.entity.booking?.costs ?? [];
  }
}

/** Validate + normalise a line item for read. Returns the same
 *  Money fields a caller would have read from the legacy single-
 *  Money shape: caller-supplied `defaultCurrency` covers items
 *  with a missing/blank currency code (older records, partial
 *  migrations). */
function validateItem(
  item: CostLineItem,
  defaultCurrency?: string | undefined,
): { amount: number; currency: string } | undefined {
  if (typeof item.amount !== 'number' || !Number.isFinite(item.amount)) return undefined;
  if (item.amount <= 0) return undefined;
  const cur = typeof item.currency === 'string' && item.currency.trim()
    ? item.currency.trim().toUpperCase()
    : defaultCurrency?.trim().toUpperCase();
  if (!cur) return undefined;
  return { amount: item.amount, currency: cur };
}

/**
 * Sum an entity's line items grouped by currency. Returns one
 * `Money` per distinct currency, sorted by code for stable
 * rendering. An entity with no items (or only invalid items)
 * returns `[]`.
 *
 * Replaces the single-`Money` return of the pre-ADR-0069
 * `getEntityCost`: cross-currency entities now surface both
 * totals rather than dropping silently.
 */
export function getEntityCost(
  bearer: CostBearing,
  defaultCurrency?: string | undefined,
): Money[] {
  return sumLineItemsByCurrency(entityLineItems(bearer), defaultCurrency);
}

function sumLineItemsByCurrency(
  items: CostLineItem[],
  defaultCurrency?: string | undefined,
): Money[] {
  const totals = new Map<string, number>();
  for (const item of items) {
    const v = validateItem(item, defaultCurrency);
    if (!v) continue;
    totals.set(v.currency, (totals.get(v.currency) ?? 0) + v.amount);
  }
  return toSortedMoneyList(totals);
}

/** Iterate every cost-bearing entity across an entire trip. Used by
 *  the trip-header rollup; the per-day version is `dayCostBearers`. */
export function tripCostBearers(trip: Trip): CostBearing[] {
  const out: CostBearing[] = [];
  for (const day of trip.days) {
    for (const place of day.places) {
      out.push({ kind: 'place', entity: place });
    }
    for (const custom of day.customs ?? []) {
      out.push({ kind: 'custom', entity: custom });
    }
  }
  for (const stay of trip.stays ?? []) {
    out.push({ kind: 'stay', entity: stay });
  }
  for (const journey of trip.journeys ?? []) {
    out.push({ kind: 'journey', entity: journey });
  }
  return out;
}

/** Cost-bearers visible on a single day. Stays + Journeys are
 *  trip-scoped but a Stay covers the day if the day's date sits in
 *  [checkIn, checkOut), and a Journey shows on its origin or
 *  destination day. We attribute to the *origin* day to avoid double-
 *  counting an overnight flight on both calendar days; rollup callers
 *  that want a different split can pass `attributeJourneyTo: 'arrive'`. */
export function dayCostBearers(
  trip: Trip,
  date: string,
  opts: { attributeJourneyTo?: 'depart' | 'arrive' | undefined; includeStays?: boolean | undefined} = {},
): CostBearing[] {
  const out: CostBearing[] = [];
  const day = trip.days.find((d) => d.date === date);
  if (day) {
    for (const place of day.places) {
      out.push({ kind: 'place', entity: place });
    }
    for (const custom of day.customs ?? []) {
      out.push({ kind: 'custom', entity: custom });
    }
  }
  const attr = opts.attributeJourneyTo ?? 'depart';
  for (const journey of trip.journeys ?? []) {
    const anchor = attr === 'arrive' ? journey.destination : journey.origin;
    if (anchor.time.slice(0, 10) === date) {
      out.push({ kind: 'journey', entity: journey });
    }
  }
  // Stays span multiple days; opt-in because attributing the full
  // booking total to a single day would inflate per-day rollups for
  // the check-in date. Caller asks explicitly when they want it
  // (e.g. trip-header total includes stays once via tripCostBearers).
  if (opts.includeStays) {
    for (const stay of trip.stays ?? []) {
      const start = stay.checkIn.slice(0, 10);
      const end = stay.checkOut.slice(0, 10);
      if (date >= start && date < end) {
        out.push({ kind: 'stay', entity: stay });
      }
    }
  }
  return out;
}

/** Sum a list of cost-bearers, grouped by ISO 4217 currency.
 *  Iterates each bearer's line items rather than its summed total
 *  so cross-currency entities contribute to every relevant bucket.
 *  Pass `itemFilter` to skip items that don't match the active
 *  participant + category chip selection — without it, a partially-
 *  matching bearer would leak its non-matching items into the total. */
export function sumByCurrency(
  bearers: CostBearing[],
  defaultCurrency?: string | undefined,
  itemFilter?: ((item: CostLineItem) => boolean) | undefined,
): Money[] {
  const totals = new Map<string, number>();
  for (const bearer of bearers) {
    for (const item of entityLineItems(bearer)) {
      if (itemFilter && !itemFilter(item)) continue;
      const v = validateItem(item, defaultCurrency);
      if (!v) continue;
      totals.set(v.currency, (totals.get(v.currency) ?? 0) + v.amount);
    }
  }
  return toSortedMoneyList(totals);
}

// Money formatting helpers were promoted to libs/ui/src/utils/money.ts
// per ADR-0076 so non-app consumers (MoneyText primitive, libs/ui)
// can format without depending on this module. Re-exported here so
// existing import paths keep resolving.
export { formatMoney, formatTotalsByCurrency } from '@kiips/ui';

// ---------------------------------------------------------------------------
// Per-line-item attribution helpers — ADR-0069.
// ---------------------------------------------------------------------------

/** Validated line items for a bearer — items that pass amount /
 *  currency checks. Used by rollups and settlement so they never
 *  hit the validation logic twice. */
export function validLineItems(
  bearer: CostBearing,
  defaultCurrency?: string | undefined,
): { item: CostLineItem; amount: number; currency: string }[] {
  const out: { item: CostLineItem; amount: number; currency: string }[] = [];
  for (const item of entityLineItems(bearer)) {
    const v = validateItem(item, defaultCurrency);
    if (!v) continue;
    out.push({ item, amount: v.amount, currency: v.currency });
  }
  return out;
}

/**
 * Read-time expansion of a line item's `splitWith`:
 *  - explicit list → filtered down to current participants.
 *  - explicit empty array → empty (cost falls entirely on payer).
 *  - absent → "split equally among all current participants."
 *
 * Same semantics as ADR-0059's `resolveSplitWith`, lifted to the
 * line-item level.
 */
export function resolveItemSplitWith(trip: Trip, item: CostLineItem): string[] {
  const participantIds = new Set((trip.participants ?? []).map((p) => p.id));
  if (item.splitWith === undefined) {
    return Array.from(participantIds);
  }
  return item.splitWith.filter((id) => participantIds.has(id));
}

/**
 * Count how the trip's line items reference a participant, so
 * the panel can warn before a cascade-prune drops the data.
 *
 *  - `asPayer`: items whose `paidBy === participantId`. These
 *    lose their attribution entirely on prune.
 *  - `asSharer`: items that list the participant in an explicit
 *    `splitWith[]`. These are kept but narrowed.
 *
 * Implicit-all (`splitWith: undefined`) doesn't count — removing
 * the participant doesn't change stored data for those items.
 */
export function countParticipantCostRefs(
  trip: Trip,
  participantId: string,
): { asPayer: number; asSharer: number } {
  let asPayer = 0;
  let asSharer = 0;
  for (const bearer of tripCostBearers(trip)) {
    for (const item of entityLineItems(bearer)) {
      if (item.paidBy === participantId) asPayer += 1;
      else if (item.splitWith?.includes(participantId)) asSharer += 1;
    }
  }
  return { asPayer, asSharer };
}

/**
 * Resolve the cost date for a bearer — the calendar day the
 * cost "happened on," used by settlement to look up a historical
 * FX rate.
 *
 *  - `place` / `custom`: the date of the containing Day.
 *  - `journey`: `origin.time` (an overnight flight is still
 *    attributed to the departure day).
 *  - `stay`: `checkIn` (the booking was incurred at check-in).
 *
 * Falls back to `trip.startDate` when the field isn't readable.
 * Always returns `YYYY-MM-DD`.
 */
export function getCostDate(bearer: CostBearing, trip: Trip): string {
  if (bearer.kind === 'place') {
    const day = trip.days.find((d) =>
      d.places.some((p) => p.id === bearer.entity.id),
    );
    return day?.date ?? trip.startDate;
  }
  if (bearer.kind === 'custom') {
    const day = trip.days.find((d) =>
      (d.customs ?? []).some((c) => c.id === bearer.entity.id),
    );
    return day?.date ?? trip.startDate;
  }
  if (bearer.kind === 'journey') {
    const time = bearer.entity.origin.time;
    if (time.length >= 10) return time.slice(0, 10);
    return trip.startDate;
  }
  // stay
  const checkIn = bearer.entity.checkIn;
  if (checkIn.length >= 10) return checkIn.slice(0, 10);
  return trip.startDate;
}

function toSortedMoneyList(inner: Map<string, number> | undefined): Money[] {
  if (!inner) return [];
  return Array.from(inner, ([currency, amount]) => ({ currency, amount })).sort((a, b) =>
    a.currency < b.currency ? -1 : a.currency > b.currency ? 1 : 0,
  );
}
